import "./NavigationTab.css";
import { useEffect, useState, useRef } from "react";
import gsap from "gsap";
import FeatureWrapper from "../../Utils/FeatureWrapper";
import { usePermissions } from "../../Utils/usePermissions";
import { useAuth } from "../../Security/AuthProvider";
import "../../Utils/FeatureWrapper.css";

// Change new data
const NavigationTab = ({
  setCurrentPage,
  agentsNames,
  setCurrentAgent,
  setShowNewClients,
  formsAllowed,
}) => {
  const [currentExpanded, setCurrentExpanded] = useState(null);
  const navigationTabRef = useRef(null);
  const calendarRef = useRef(null);
  const settingsRef = useRef(null);
  const newDataRef = useRef(null);
  const [showNewClientsDropdown, setShowNewClientsDropdown] = useState(false);
  
  const { getCredentials } = useAuth();
  const credentials = getCredentials();
  const { hasFeature, getCurrentPlan, loading } = usePermissions(credentials?.company_name);

  function expandSection(ref) {
    if (currentExpanded) {
      gsap.to(currentExpanded.current, {
        maxHeight: 0,
        paddingTop: 0,
        duration: 0.5,
        ease: "power2.inOut",
        onComplete: () => {
          // This ensures the animation completes before state changes
        },
      });
    }

    // Use setTimeout to ensure the state update happens after the animation
    setTimeout(() => {
      if (currentExpanded !== ref) {
        setCurrentExpanded(ref);
      } else {
        setCurrentExpanded(null);
      }
    }, 0);
  }

  useEffect(() => {
    let animation;
    if (currentExpanded) {
      animation = gsap.to(currentExpanded.current, {
        maxHeight: 500,
        duration: 0.5,
        paddingTop: "1px",
        ease: "power2.inOut",
      });
    }

    return () => {
      if (animation) animation.kill();
    };
  }, [currentExpanded]);

  // Afficher un indicateur de chargement pendant la récupération des permissions
  if (loading) {
    return (
      <div className="navigation-tab" ref={navigationTabRef}>
        <div className="navigation-tab-title">
          <p>AutoPlanneur</p>
        </div>
        <div className="loading-permissions">
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="navigation-tab" ref={navigationTabRef}>
      <div className="navigation-tab-title">
        <p>AutoPlanneur</p>
      </div>
      
      <div className="expandable-button-container">
        <div
          className="expandable-button calendar-menu"
          onClick={() => expandSection(calendarRef)}
        >
          <div className="expandable-button-title-up-bar"></div>
          <p className="expandable-button-title">Agendas</p>

          <div className="expandable-button-content" ref={calendarRef}>
            {agentsNames.length > 0 &&
              agentsNames.map((agentName) => (
                <p
                  key={agentName}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentPage("calendar");
                    setCurrentAgent(agentName);
                  }}
                  className="expandable-button-content-item"
                >
                  {agentName}
                </p>
              ))}
          </div>
          <div className="expandable-button-title-bar"></div>
        </div>
        
        <div
          className="expandable-button settings-menu"
          onClick={() => expandSection(settingsRef)}
        >
          <div className="expandable-button-title-container">
            <p className="expandable-button-title">Paramètres</p>
          </div>
          <div className="expandable-button-content" ref={settingsRef}>
            <>
              <p
                onClick={() => setCurrentPage("agents")}
                className="expandable-button-content-item"
              >
                Agents
              </p>
              <p
                onClick={() => setCurrentPage("operations")}
                className="expandable-button-content-item"
              >
                Types de missions
              </p>
              <p
                onClick={() => setCurrentPage("roles")}
                className="expandable-button-content-item"
              >
                Qualifications
              </p>
              
              {/* Formulaires avec gestion des permissions */}
              <FeatureWrapper 
                feature="form_creation"
                showFallback={false}
              >
                <p
                  onClick={() => setCurrentPage("forms")}
                  className="expandable-button-content-item"
                >
                  Formulaires
                </p>
              </FeatureWrapper>
            </>
          </div>
          <div className="expandable-button-title-bar"></div>
        </div>
        
        <div
          className="expandable-button new-data-menu"
          onClick={() => expandSection(newDataRef)}
        >
          <p className="expandable-button-title" ref={newDataRef}>
            Nouveaux clients
          </p>
          <div className="expandable-button-content" ref={newDataRef}>
            <p
              onClick={(e) => {
                e.stopPropagation();
                setCurrentPage("nouveauClientUnitaire");
                setShowNewClients(true);
              }}
              className="expandable-button-content-item"
            >
              Client unique
            </p>
            
            {/* Masquer l'import CSV si fonctionnalité bulk non autorisée */}
            <FeatureWrapper 
              feature="bulk_operations"
              showFallback={false}
            >
            <p
              onClick={(e) => {
                e.stopPropagation();
                setCurrentPage("nouveauClientsCSV");
                setShowNewClients(true);
              }}
              className="expandable-button-content-item"
            >
              Import CSV
            </p>
            </FeatureWrapper>
          </div>
          <div className="expandable-button-title-bar"></div>
        </div>
        
        <div
          className="expandable-button clients-menu"
          onClick={() => setCurrentPage("clients")}
        >
          <p className="expandable-button-title">Clients</p>
          <div className="expandable-button-title-bar"></div>
        </div>
        
        <div
          className="expandable-button recent-missions-menu"
          onClick={() => setCurrentPage("recentMissions")}
        >
          <p className="expandable-button-title">Missions Récentes</p>
          <div className="expandable-button-title-bar"></div>
        </div>
        
        <div
          className="expandable-button archive-menu"
          onClick={() => setCurrentPage("archive")}
        >
          <p className="expandable-button-title">Archive</p>
          <div className="expandable-button-title-bar"></div>
        </div>
        
        {/* Section statistiques - uniquement pour Premium */}
        <FeatureWrapper 
          feature="ai_insights"
          showFallback={false}
        >
          <div
            className="expandable-button statistics-menu"
            onClick={() => setCurrentPage("statistics")}
          >
            <p className="expandable-button-title">Statistiques</p>
            <div className="expandable-button-title-bar"></div>
          </div>
        </FeatureWrapper>
        
        {/* Section facturation simplifiée */}
        <FeatureWrapper 
          feature="advanced_stats"
          showFallback={false}
        >
          <div
            className="expandable-button billing-menu"
            onClick={() => setCurrentPage("billing")}
          >
            <p className="expandable-button-title">Facturation</p>
            <div className="expandable-button-title-bar"></div>
          </div>
        </FeatureWrapper>
      </div>
    </div>
  );
};

export default NavigationTab;
